/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import TypePieChart from "@/components/Dashboard/TypePieChart";

import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Skeleton } from "@/components/ui/skeleton";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { Project } from "@/types";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import slugify from "slugify";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileBadge, FileDown, Info } from "lucide-react";
import { CarouselSkeleton, PieSkeleton } from "./loadingSkeleton";
const LazyProject = lazy(() => import("../project/components/CarouselProject"));
const LazyDataCardV2 = lazy(
  () => import("@/components/Dashboard/StatsDashboardV2")
);
const LazyChart = lazy(() => import("@/components/Dashboard/ChartDashboard"));
const LazyMap = lazy(
  () => import("@/project-owner/components/MapClusterPrograms")
);

import { Table, TableBody, TableRow } from "@/components/ui/table";
import {
  readFundingFinancierId,
  resolveFundingEuroAmount,
  type RealFundingSummary,
} from "@/hooks/useFunding";

type RealFundingProjectEntry = {
  id?: number | string;
  country?: { currency?: { code?: string | null } };
  realFundings?: RealFundingSummary[];
};

type RealFundingByProjectPayload = {
  data?: RealFundingProjectEntry[];
};

type FundingDistributionEntry = {
  id?: number | string;
  name?: string;
  total: number;
  obtained: number;
  pourcent?: number;
  currency?: unknown;
};

const HOUR_IN_MS = 1000 * 60 * 60;
const TEN_MINUTES_IN_MS = 1000 * 60 * 10;
const DEFAULT_DASHBOARD_OPTION = "Projet";
const DASHBOARD_OPTION_STORAGE_KEY = "DashOption";

const normalizeFundingPercentages = (entries: FundingDistributionEntry[]) => {
  const denominator = entries.reduce(
    (sum, entry) => sum + Number(entry?.obtained || 0),
    0
  );

  if (denominator <= 0) {
    return entries.map((entry) => ({ ...entry, pourcent: 0 }));
  }

  const ranked = entries.map((entry, index) => {
    const rawPercent = (entry.obtained / denominator) * 100;
    const floorPercent = Math.floor(rawPercent);

    return {
      entry,
      index,
      remainder: rawPercent - floorPercent,
      percent: floorPercent,
    };
  });

  let pointsToDistribute =
    100 - ranked.reduce((sum, item) => sum + item.percent, 0);

  if (pointsToDistribute > 0) {
    ranked
      .sort(
        (a, b) =>
          b.remainder - a.remainder ||
          b.entry.obtained - a.entry.obtained ||
          a.index - b.index
      )
      .forEach((item) => {
        if (pointsToDistribute <= 0) return;
        item.percent += 1;
        pointsToDistribute -= 1;
      });
  }

  if (pointsToDistribute < 0) {
    ranked
      .sort(
        (a, b) =>
          a.remainder - b.remainder ||
          a.entry.obtained - b.entry.obtained ||
          b.index - a.index
      )
      .forEach((item) => {
        if (pointsToDistribute >= 0 || item.percent <= 0) return;
        item.percent -= 1;
        pointsToDistribute += 1;
      });
  }

  return ranked
    .sort((a, b) => a.index - b.index)
    .map((item) => ({ ...item.entry, pourcent: item.percent }));
};

const getFundingOptionLabel = (option: string | null) => {
  switch (option) {
    case "PP":
      return "Porteur de Projet";
    case "Pays":
      return "Pays";
    case "type":
      return "Type de Projet";
    case "Projet":
    default:
      return "Projet";
  }
};

const Dashboard = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "common",
  });
  const { isAuthenticated, user, loading } = useAuth();
  const { firstname: url } = useParams();
  const navigate = useNavigate();
  const {
    data: projectData,
    loading: loadingProject,
    fetchData,
  } = useFetchDataV2<Project[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/financer/projects`,
    `projectsDash`,
    HOUR_IN_MS
  );
  const { data: realFundingByProjectData } =
    useFetchDataV2<RealFundingByProjectPayload>(
      `${import.meta.env.VITE_API_ENDPOINT}/real-fundings?by=project`,
      `real-fundings-by-project-dashboard`,
      HOUR_IN_MS
    );
  const [fundingOptions, setFundingOptions] = useState(
    localStorage.getItem(DASHBOARD_OPTION_STORAGE_KEY) !== null
      ? localStorage.getItem(DASHBOARD_OPTION_STORAGE_KEY)
      : DEFAULT_DASHBOARD_OPTION
  );
  const [fundingData, setFundingData] = useState([]);
  const [docs, setDocs] = useState([]);
  const [cerfa, setCerfa] = useState([]);
  const [typesDocsData, setTypesDocsData] = useState([]);
  const realFundingsByProject = useMemo(() => {
    const map = new Map<
      number,
      { contractCurrencyCode: string; fundings: RealFundingSummary[] }
    >();
    const rows = Array.isArray(realFundingByProjectData?.data)
      ? realFundingByProjectData.data
      : [];

    for (const row of rows) {
      const pid = Number(row?.id);
      if (!Number.isFinite(pid)) continue;
      map.set(pid, {
        contractCurrencyCode: String(row?.country?.currency?.code ?? ""),
        fundings: Array.isArray(row?.realFundings) ? row.realFundings : [],
      });
    }

    return map;
  }, [realFundingByProjectData]);

  const sumRealFundingForProject = async (project: Project) => {
    const source = realFundingsByProject.get(Number(project?.id));
    if (!source) return 0;
    const contractCurrencyCode = source.contractCurrencyCode;
    const fundings = source.fundings;
    let total = 0;

    for (const funding of fundings) {
      const financierId = readFundingFinancierId(funding);
      if (Number(financierId) !== Number(user?.id)) continue;
      total += await resolveFundingEuroAmount(funding, contractCurrencyCode);
    }

    return total;
  };

  const projects = useMemo(() => {
    if (!projectData) return [];

    const enrichedProjects = projectData.map((project) => {
      const latestInvoiceDate = (project.verdeoFundingInvoiceds || []).reduce(
        (latest, invoice) => {
          const time = new Date(invoice?.at?.date).getTime();
          return time > latest ? time : latest;
        },
        0
      );

      return {
        ...project,
        _latestInvoiceDate: latestInvoiceDate,
      };
    });

    const sorted = [...enrichedProjects].sort(
      (a, b) => b._latestInvoiceDate - a._latestInvoiceDate
    );

    return sorted.map(({ _latestInvoiceDate, ...rest }) => rest);
  }, [projectData]);

  const reportEndpoint = useMemo(() => {
    if (!projects?.length) return null;
    const ids = projects.map((p) => p.id).filter(Boolean);
    if (!ids.length) return null;
    return `${import.meta.env.VITE_API_ENDPOINT}/reporting?context=dashboard&projects=${ids.join(
      ","
    )}`;
  }, [projects]);

  const { data: reportData, loading: loadingReport } = useFetchDataV2<any>(
    reportEndpoint,
    `reporting-financeur-${projects?.length ?? 0}`,
    TEN_MINUTES_IN_MS
  );
  const reportingQueryKey = useMemo(
    () =>
      reportEndpoint
        ? [
          `reporting-financeur-${projects?.length ?? 0}`,
          reportEndpoint,
        ]
        : [],
    [projects?.length, reportEndpoint]
  );
  const visibleDashboardIndicatorIds = useMemo(() => {
    const totals = reportData?.projectTotalIndicators ?? [];

    return Array.from(
      new Set(
        totals
          .filter(
            (ind: any) =>
              ind?.type === 1 &&
              ind?.indicator?.programReporting !== null &&
              ind?.indicator?.dashboard === true &&
              ind?.indicator?.code !== "euro_recus"
          )
          .map((ind: any) => ind.indicator.id)
      )
    );
  }, [reportData]);

  useEffect(() => {
    if (!projectData) return;
    (async () => {
      const docsData = await fetchData(
        `${import.meta.env.VITE_API_ENDPOINT}/docs/financer`
      );
      setDocs(Object.values(docsData).flat());

      const cerfaData = await fetchData(
        `${import.meta.env.VITE_API_ENDPOINT}/certificats`
      );
      setCerfa(Object.values(cerfaData).flat());

      try {
        const response = await fetch(
          `${import.meta.env.VITE_IP_API}/types-docs.json`
        );
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        const data = await response.json();
        if (data) {
          const docTypes = data.flatMap((type) =>
            (type?.children || []).map((child) => ({
              value: child.value,
              label: child.label,
              icon: child.icon,
            }))
          );
          setTypesDocsData(docTypes);
        }
      } catch (error) {
        console.error("Erreur lors du fetch :", error);
      }
    })();
  }, [projectData]);

  useEffect(() => {
    if (
      !loading &&
      user?.fullname &&
      `${slugify(user.fullname, { lower: true, trim: true })}` !== url
    ) {
      navigate("/error", { replace: true });
    }
  }, [loading, isAuthenticated, user, url, navigate]);

  useEffect(() => {
    let isMounted = true;

    if (!user || !projects || projects.length === 0) {
      return;
    }

    (async () => {
      const fundingProjectMap = new Map();
      const fundingPPMap = new Map();
      const fundingCountryMap = new Map();
      const fundingTypeMap = new Map();

      for (const project of projects ?? []) {
        const real = await sumRealFundingForProject(project);

        const projectEntry = fundingProjectMap.get(project?.id) || {
          id: project?.id,
          name: project?.name,
          total: 0,
          obtained: 0,
        };
        projectEntry.total += real;
        projectEntry.obtained += real;
        fundingProjectMap.set(project?.id, projectEntry);

        const ppId = project?.program?.carrier?.id;
        const ppEntry = fundingPPMap.get(ppId) || {
          id: ppId,
          name: project?.program?.carrier?.fullname,
          total: 0,
          obtained: 0,
        };
        ppEntry.total += real;
        ppEntry.obtained += real;
        fundingPPMap.set(ppId, ppEntry);

        const countryId = project?.country?.id;
        const countryEntry = fundingCountryMap.get(countryId) || {
          id: countryId,
          name: project?.country?.name,
          currency: project?.country?.currency,
          total: 0,
          obtained: 0,
        };
        const typeId = project?.type?.id;
        const typeEntry = fundingTypeMap.get(typeId) || {
          id: typeId,
          name: project?.type?.name,
          total: 0,
          obtained: 0,
          pourcent: 0,
        };
        typeEntry.total += real;
        typeEntry.obtained += real;
        fundingTypeMap.set(typeId, typeEntry);

        countryEntry.total += real;
        countryEntry.obtained += real;
        fundingCountryMap.set(countryId, countryEntry);
      }

      const selectedEntries =
        fundingOptions === "PP"
          ? Array.from(fundingPPMap.values())
          : fundingOptions === "Pays"
            ? Array.from(fundingCountryMap.values())
            : fundingOptions === "type"
              ? Array.from(fundingTypeMap.values())
              : Array.from(fundingProjectMap.values());

      if (isMounted) {
        setFundingData(normalizeFundingPercentages(selectedEntries));
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [user?.id, projects, fundingOptions, realFundingsByProject]);

  const invoicesByDate = useMemo(() => {
    const invoices = (docs || []).map((doc) => {
      const project = projects?.find((item) => item.id === doc?.project);
      const type = typesDocsData?.find((item) => item.value === doc?.type);
      return {
        PP: project?.program?.carrier?.fullname,
        project: project?.name,
        at: new Date(doc?.at?.date),
        name: doc?.name,
        path: doc?.path,
        type: type?.label,
      };
    });

    const list = invoices
      .sort((a, b) => b.at.getTime() - a.at.getTime())
      .slice(0, 10)
      .map((invoice) => ({
        ...invoice,
        at: new Date(invoice?.at).toLocaleDateString("fr-FR"),
      }));

    return list;
  }, [projects, docs, typesDocsData]);

  const cerfaByDate = useMemo(() => {
    const cerfaList = (cerfa || [])
      .filter((item) => item.financer.id == user?.id)
      .map((item) => {
        const [start, end] = item?.dateVersement?.split("#");
        return {
          project: item?.project?.name,
          duration: `Du ${start} au ${end}`,
          financer: item?.financer?.id,
          date: new Date(start),
          name: item?.cerfa,
        };
      });

    const list = cerfaList
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 10)
      .map((item) => ({
        ...item,
        at: new Date(item?.date).toLocaleDateString("fr-FR"),
      }));

    return list;
  }, [cerfa, user?.id]);
  return (
    <>
      <div className="my-5">
        <h1 className=" my-5 text-2xl font-medium">{t("Tableau de bord")}</h1>
        <h2
          className="text-green-900  font-semibold  inline-block"
          id="mes-programs"
        >
          {"Projets Soutenus"}
        </h2>
        {loadingProject ? (
          <CarouselSkeleton />
        ) : (
          // <div style={{ width: "calc(-340px + 100vw)", margin: "auto" }}>
          <Carousel
            opts={{
              align: "start",
            }}
            className="my-5 mx-8 w-auto"
          >
            <CarouselContent>
              {projects?.map((project, key: number) => (
                <Suspense
                  key={key}
                  fallback={
                    <Skeleton className="group relative flex aspect-[255/200]  overflow-hidden rounded-[15px] bg-foreground" />
                  }
                >
                  <CarouselItem
                    key={key}
                    className="md:basis-1/5 lg:basis-1/6 xl:basis-[13%]"
                  >
                    <div className="p-1">
                      <LazyProject project={project} />
                    </div>
                  </CarouselItem>
                </Suspense>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        )}
      </div>
      <div className="flex flex-row gap-4">
        <div className="w-1/2">
          <div className="overflow-hidden rounded-[20px] w-full mt-5">
            <Suspense fallback={"Chargement"}>
              <LazyMap projectsSelected={projects} />
            </Suspense>
          </div>
          {!loadingProject && fundingData && fundingData?.length > 0 ? (
            <div className="w-full mt-5 rounded-[20px] border-4 border-[#ecedee] p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className=" text-green-900 font-bold m-0">
                  Repartition des Dons selon {getFundingOptionLabel(fundingOptions).toLowerCase()} :
                </h3>
                <ParamOptions
                  options={fundingOptions}
                  setOptions={setFundingOptions}
                />
              </div>
              <div className="w-full p-5">
                <TypePieChart data={fundingData} />
              </div>
            </div>
          ) : (
            <div className="w-full mt-5 rounded-[20px] border-4 border-[#ecedee] p-5">
              <PieSkeleton />
            </div>
          )}
        </div>
        <div className="w-1/2">
          <div id="data-reporting" className=" md:w-full pb-5">
            <Suspense
              fallback={
                <div className="flex flex-wrap gap-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton
                      key={i}
                      className="rounded-[15px] w-[165px] h-[100px] bg-[#ecedee]"
                    />
                  ))}
                </div>
              }
            >
              <LazyDataCardV2
                projects={projects}
                reportData={reportData}
                reportLoading={loadingReport}
              />
            </Suspense>
          </div>
          {/* <TabInvoice
            Invoices={invoicesByDate}
            loading={loadingProject}
            financer={user?.fullname}
            cerfa={cerfaByDate}
          /> */}
          <div>
            <Tabs defaultValue="Invoice" className="w-full">
              <TabsList className="mb-2 grid w-full grid-cols-2 bg-background">
                <TabsTrigger
                  className="rounded-none border-b-2 text-[#626262] data-[state=active]:border-foreground data-[state=active]:shadow-none"
                  value="Invoice"
                >
                  Mes Derniers Documents
                </TabsTrigger>
                <TabsTrigger
                  className="rounded-none border-b-2 text-[#626262] data-[state=active]:border-foreground data-[state=active]:shadow-none"
                  value="Cerfa"
                >
                  Mes reçus fiscaux
                </TabsTrigger>
              </TabsList>
              <TabsContent value="Invoice">
                <TabDashboard
                  loading={loadingProject}
                  financer={url}
                  data={invoicesByDate}
                  mode="doc"
                />
              </TabsContent>
              <TabsContent value="Cerfa">
                <TabDashboard
                  loading={loadingProject}
                  financer={url}
                  data={cerfaByDate}
                  mode="cerfa"
                />
              </TabsContent>
            </Tabs>
          </div>
          {/* <div className="w-full mt-5">
            <Suspense>
              <LazyChartV />
            </Suspense>
          </div> */}
        </div>
      </div>
      <div>
        <div className="flex flex-wrap mt-5">
          <Suspense>
            <LazyChart
              reportData={reportData}
              reportLoading={loadingReport}
              visibleIndicatorIds={visibleDashboardIndicatorIds}
              reportingQueryKey={reportingQueryKey}
            />
          </Suspense>
        </div>
      </div>
    </>
  );
};

export default Dashboard;

const ParamOptions = ({ options, setOptions }) => {
  return (
    <div className="grid gap-4">
      <div className="space-y-2">
        <Select
          onValueChange={(e) => {
            setOptions(e);
            localStorage.setItem(DASHBOARD_OPTION_STORAGE_KEY, e);
          }}
        >
          <SelectTrigger className="text-foreground/90 outline-none focus:ring-0 focus:ring-transparent">
            <SelectValue placeholder={getFundingOptionLabel(options)} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="Projet"> Projet </SelectItem>
              <SelectItem value="PP"> Porteur de Projet </SelectItem>
              <SelectItem value="Pays">Pays </SelectItem>
              <SelectItem value="type">Type de Projet</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-2"></div>
    </div>
  );
};

const TabDashboard = ({ data, loading, financer, mode }) => {
  const handleDownload = async (file: string) => {
    try {
      const token =
        localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
      const filename = encodeURIComponent(file);
      const response = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT
        }/download-certificate?filename=${filename}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`, // Remplace par ton vrai token ou méthode d'accès
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors du téléchargement");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = file;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url); // Nettoyage
    } catch (error) {
      console.error("Erreur :", error);
      alert("Échec du téléchargement du fichier");
    }
  };

  return (
    <Table>
      <TableBody>
        <ScrollArea className="h-[280px] w-full ">
          {loading && (
            <div>
              <Skeleton className=" h-10 rounded-xl w-full mb-5" />
              <Skeleton className=" h-10 rounded-xl w-full mb-5" />
              <Skeleton className=" h-10 rounded-xl w-full mb-5" />
              <Skeleton className=" h-10 rounded-xl w-full mb-5" />
              <Skeleton className=" h-10 rounded-xl w-full mb-5" />
              <Skeleton className=" h-10 rounded-xl w-full" />
            </div>
          )}
          {!loading && data.length === 0 ? (
            <p className="text-center my-10">
              {mode == "doc" ? "Pas de Factures" : "Pas de certificats"}
            </p>
          ) : (
            data?.map((invoice: any) => (
              <TableRow className="hover:bg-gray-200 flex flex-row items-center justify-between">
                <div className="flex flex-row gap-4 m-5 items-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Info
                        size={18}
                        className="cursor-pointer font-bold text-[#292d42]"
                        style={{
                          marginTop: "5px",
                          marginRight: "8px",
                          marginLeft: "-11px",
                        }}
                      />
                    </PopoverTrigger>
                    <PopoverContent className="z-[99999] text-xs">
                      <p className="mx-0 mb-2">
                        {" "}
                        <span className="font-bold">Projet : </span>{" "}
                        {invoice?.project}
                      </p>
                      {mode == "doc" && (
                        <p className="mx-0 mb-2">
                          {" "}
                          <span className="font-bold">
                            Porteur de Projet :{" "}
                          </span>
                          {invoice?.PP}
                        </p>
                      )}
                      <p className="mx-0 mb-2">
                        {" "}
                        <span className="font-bold">
                          {mode == "doc" ? "Date :" : "Date de versement :"}{" "}
                        </span>{" "}
                        {mode == "doc" ? invoice?.at : invoice?.duration}
                      </p>
                      {mode == "doc" && (
                        <p className="mx-0 mb-2">
                          {" "}
                          <span className="font-bold">Type : </span>{" "}
                          {invoice?.type}
                        </p>
                      )}
                    </PopoverContent>
                  </Popover>
                  <p className="m-0"> {invoice?.name?.slice(0, 25)}</p>
                </div>
                {mode == "doc" ? (
                  <a
                    href={`${import.meta.env.VITE_IP_API}/media/${invoice?.path
                      }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="  mr-5 hover:underline"
                  >
                    <FileDown />
                  </a>
                ) : (
                  <button
                    onClick={() => {
                      handleDownload(invoice?.name);
                    }}
                    className="mr-5"
                  >
                    <FileBadge />
                  </button>
                )}
              </TableRow>
            ))
          )}
        </ScrollArea>
        <TableRow className="flex justify-end mt-4">
          <Button>
            <a
              href={
                mode == "doc"
                  ? `/${financer}/mes-documents`
                  : `/${financer}/mes-certificats`
              }
            >
              Voir Plus
            </a>
          </Button>
        </TableRow>
      </TableBody>
    </Table>
  );
};
